import { FTDI, FTDIOptions } from "./ftdi"

/**
 * Returns a promise that resolves after the specified number of milliseconds.
 * @param ms Milliseconds to delay
 */
function delay(ms: number): Promise<void> {
	return new Promise((resolve) => setTimeout(resolve, ms))
}

/**
 * Extracts the first valid JSON object or array from a string.
 * Throws if no JSON is found.
 * @param text Raw response text
 * @returns The JSON substring
 */
function extractJson(text: string): string {
	const jsonRegex = /({.*}|\[.*\])/s
	const match = RegExp(jsonRegex).exec(text)
	if (!match) {
		throw new Error(`No JSON object or array found in response: ${text}`)
	}
	return match[0]
}

function extractOutputFromLines(text: string, command: string): string {
	const outputRegex = new RegExp(`${command}([\\s\\S]*?)${command} Done`, "m")
	const match = outputRegex.exec(text)
	if (!match) {
		throw new Error(`No output found in response: ${text}`)
	}
	return match[1].trim()
}

/**
 * High-level FTDI JSON command helper for sending string commands to an FTDI device and receiving JSON responses.
 *
 * This class wraps a low-level FTDI instance and provides:
 * - Automatic dynamic endpoint discovery
 * - Robust device connection and read lifecycle management
 * - Event-driven data accumulation
 * - High-level JSON command interface with retry and exponential backoff for fragmented responses
 * - Convenience methods for initialization and closing
 */
export class FTDIJson {
	private readonly ftdi: FTDI

	constructor(device: USBDevice, options: FTDIOptions) {
		this.ftdi = new FTDI(device, options)
	}

	/**
	 * Initializes the FTDI device and prepares it for communication.
	 * Opens the device, claims interface, sets baud rate, flushes buffers, and starts the read loop.
	 * Throws if initialization fails.
	 */
	async initialize(): Promise<void> {
		await this.ftdi.initialize()
	}

	/**
	 * Sends a string command to the FTDI device and awaits a JSON-parsable response.
	 *
	 * Handles fragmented or delayed responses by waiting for a configurable "quiet window" (ms of no new data).
	 * If JSON parsing fails, retries with an exponentially increasing quiet window up to `maxRetries` times.
	 *
	 * @template T The expected type of the parsed JSON response.
	 * @param opts Options object
	 * @param opts.command The command string to send (will be suffixed with a carriage return if not present).
	 * @param opts.responseTimeoutMilliseconds Optional. Max ms to wait for a response before giving up. Default: 10,000 ms.
	 * @param opts.quietWindowMilliseconds Optional. Initial ms of no new data to consider the response complete. Default: 3,000 ms.
	 * @param opts.maxRetries Optional. Number of times to retry with a longer quiet window if JSON parsing fails. Default: 3.
	 * @returns The parsed JSON response as type T.
	 * @throws If no valid JSON is found after all retries, or if the device disconnects.
	 */
	/**
	 * Sends a string command to the FTDI device and awaits a JSON-parsable response.
	 *
	 * @template T The expected type of the parsed JSON response.
	 * @param command The command string to send (will be suffixed with a carriage return if not present).
	 * @param options Optional settings: responseTimeoutMilliseconds, quietWindowMilliseconds, maxRetries.
	 * @returns The parsed JSON response as type T.
	 * @throws If no valid JSON is found after all retries, or if the device disconnects.
	 */
	async sendJsonCommand<T = any>(
		command: string,
		options?: SendJsonCommandOptions,
	): Promise<T> {
		const responseTimeoutMilliseconds =
			options?.responseTimeoutMilliseconds ?? 10_000
		const quietWindowMilliseconds = options?.quietWindowMilliseconds ?? 3_000
		const maxRetries = options?.maxRetries ?? 3
		let attempt = 0
		let lastError: Error | null = null
		let currentQuietWindow = quietWindowMilliseconds
		while (attempt < maxRetries) {
			let responseText = ""
			let responseComplete = false
			let responseTimer: NodeJS.Timeout | null = null

			const dataHandler = (event: CustomEvent) => {
				const data = event.detail
				const decoder = new TextDecoder()
				const text = decoder.decode(data)
				responseText += text
				if (responseTimer) clearTimeout(responseTimer)
				responseTimer = setTimeout(() => {
					responseComplete = true
				}, currentQuietWindow)
			}

			this.ftdi.addEventListener("data", dataHandler as EventListener)

			const commandBytes = new TextEncoder().encode(
				command.endsWith("\r") ? command : command + "\r",
			)
			await this.ftdi.writeAsync(commandBytes)

			const startTime = Date.now()
			while (
				!responseComplete &&
				Date.now() - startTime < responseTimeoutMilliseconds
			) {
				await delay(100)
			}

			this.ftdi.removeEventListener("data", dataHandler as EventListener)
			if (responseTimer) clearTimeout(responseTimer)

			try {
				const jsonString = extractJson(responseText)
				const result = JSON.parse(jsonString) as T

				console.debug(
					`[FTDIJson] JSON response: ${JSON.stringify(
						{
							result,
							jsonString,
							attempt,
							currentQuietWindow,
						},
						null,
						2,
					)}`,
				)
				return result
			} catch (err) {
				lastError = new Error(
					`Invalid JSON response (attempt ${attempt + 1}): ${responseText}: ${err}`,
				)
				currentQuietWindow *= 2 // Exponential backoff
				attempt++
			}
		}
		throw lastError ?? new Error("Failed to parse JSON after retries")
	}

	async sendCommand<T = any>(
		command: string,
		options?: SendJsonCommandOptions,
	): Promise<T> {
		const responseTimeoutMilliseconds =
			options?.responseTimeoutMilliseconds ?? 10_000
		const quietWindowMilliseconds = options?.quietWindowMilliseconds ?? 3_000
		const maxRetries = options?.maxRetries ?? 3
		let attempt = 0
		let lastError: Error | null = null
		let currentQuietWindow = quietWindowMilliseconds
		while (attempt < maxRetries) {
			let responseText = ""
			let responseComplete = false
			let responseTimer: NodeJS.Timeout | null = null

			const dataHandler = (event: CustomEvent) => {
				const data = event.detail
				const decoder = new TextDecoder()
				const text = decoder.decode(data)
				responseText += text
				if (responseTimer) clearTimeout(responseTimer)
				responseTimer = setTimeout(() => {
					responseComplete = true
				}, currentQuietWindow)
			}

			this.ftdi.addEventListener("data", dataHandler as EventListener)

			const commandBytes = new TextEncoder().encode(
				command.endsWith("\r") ? command : command + "\r",
			)
			await this.ftdi.writeAsync(commandBytes)

			const startTime = Date.now()
			while (
				!responseComplete &&
				Date.now() - startTime < responseTimeoutMilliseconds
			) {
				await delay(100)
			}

			this.ftdi.removeEventListener("data", dataHandler as EventListener)
			if (responseTimer) clearTimeout(responseTimer)

			try {
				const jsonString = extractOutputFromLines(responseText, command)
				const result = createJsonFromLines(jsonString) as T

				console.debug(
					`[FTDIJson] JSON response: ${JSON.stringify(
						{
							result,
							jsonString,
							attempt,
							currentQuietWindow,
						},
						null,
						2,
					)}`,
				)
				return result
			} catch (err) {
				lastError = new Error(
					`Invalid JSON response (attempt ${attempt + 1}): ${responseText}: ${err}`,
				)
				currentQuietWindow *= 2 // Exponential backoff
				attempt++
			}
		}
		throw lastError ?? new Error("Failed to parse JSON after retries")
	}

	/**
	 * Closes the FTDI device and releases resources.
	 */
	async close(): Promise<void> {
		await this.ftdi.closeAsync()
	}
}

function createJsonFromLines(lines: string) {
	const data = lines
		.split("\n")
		.map((line) => line.trim())
		.filter((line) => line !== "")
		.filter((line) => line.includes("="))
		.map((line) => line.split("=", 2))
		.reduce(
			(acc, [key, value]) => {
				acc[key] = value.replace(/"/g, "")
				return acc
			},
			{} as Record<string, string>,
		)
	return {
		data,
		success: true,
		message: undefined,
	}
}
export type SendJsonCommandOptions = {
	responseTimeoutMilliseconds?: number
	quietWindowMilliseconds?: number
	maxRetries?: number
}
/**
 * Finds a USB device by serial number and returns an initialized FTDIJson instance.
 *
 * @param serialNumber The serial number of the target USB device.
 * @param options Options for FTDI device initialization (baud rate, buffer size, etc).
 * @returns An initialized FTDIJson instance ready for communication.
 * @throws If no device with the given serial number is found.
 */
export async function getFTDIJsonBySerial(
	serialNumber: string,
	options: FTDIOptions,
): Promise<FTDIJson> {
	const device = (await navigator.usb.getDevices()).find(
		(d) => d.serialNumber === serialNumber,
	)
	if (!device)
		throw new Error(`No USB device with serial number ${serialNumber} found`)
	const ftdiJson = new FTDIJson(device, options)
	await ftdiJson.initialize()
	return ftdiJson
}
